import { useEffect, useMemo, useState } from "react";
import { useReducedMotion } from "framer-motion";

export type TypewriterPhase = "typing" | "dwell" | "erasing";

export interface UseTypewriterOptions {
  /** When true, cycle through `messages` (type → dwell → backspace → next).
   * When false, type the first message once and hold. Default true when more
   * than one message is given. */
  rotate?: boolean;
}

interface TypewriterState {
  text: string;
  phase: TypewriterPhase;
  messageIndex: number;
}

// DESIGN.md §5 canonical timings. Speed normalises on total duration so en and
// zh cadence match; erase runs in 2-char chunks so ticks stay near the frame
// budget instead of committing a render every 10ms.
const TOTAL_TYPE_MS = 550;
const MIN_CHAR_MS = 18;
const MAX_CHAR_MS = 70;
const DWELL_MS = 2400; // 4 caret blink cycles at 600ms
const ERASE_TICK_MS = 20;
const ERASE_CHARS_PER_TICK = 2;

const SEP = String.fromCharCode(0);

/**
 * Letter-by-letter typewriter state machine for loading surfaces.
 * type → dwell (caret blinks) → backspace → next message.
 *
 * - If the target message changes mid-flight (e.g. auth phase change), the
 *   current text backspaces to the common prefix, then types the new message.
 * - Reduced motion: the current message shows instantly, static, no rotation.
 */
export function useTypewriter(
  messages: readonly string[],
  { rotate = messages.length > 1 }: UseTypewriterOptions = {},
): TypewriterState {
  // Key on content so a new array identity with the same strings does not
  // restart the animation (locale hooks tend to rebuild arrays every render).
  const messagesKey = messages.join(SEP);
  const stableMessages = useMemo(() => messagesKey.split(SEP), [messagesKey]);

  const reducedMotion = useReducedMotion() ?? false;

  const [state, setState] = useState<TypewriterState>({
    text: "",
    phase: "typing",
    messageIndex: 0,
  });

  const { text, phase, messageIndex } = state;
  const target = stableMessages[messageIndex % stableMessages.length] ?? "";

  useEffect(() => {
    if (reducedMotion) return; // handled by the render-time override below

    const eraseTick = () => {
      const id = window.setTimeout(() => {
        setState((s) => ({
          ...s,
          text: s.text.slice(0, -ERASE_CHARS_PER_TICK),
          phase: "erasing",
        }));
      }, ERASE_TICK_MS);
      return () => window.clearTimeout(id);
    };

    if (phase === "erasing") {
      if (rotate) {
        // Rotation erase: all the way to empty, then advance.
        if (text.length > 0) return eraseTick();
        setState((s) => ({
          text: "",
          phase: "typing",
          messageIndex: (s.messageIndex + 1) % stableMessages.length,
        }));
        return;
      }
      // Converging erase (target changed, e.g. auth phase swap): backspace to
      // the shared prefix, then resume typing.
      if (!target.startsWith(text) && text.length > 0) return eraseTick();
      setState((s) => ({ ...s, phase: "typing" }));
      return;
    }

    // Target changed under us while typing or dwelling.
    if (!target.startsWith(text) && text.length > 0) {
      setState((s) => ({ ...s, phase: "erasing" }));
      return;
    }

    if (phase === "typing") {
      if (text.length < target.length) {
        const charMs = Math.min(
          MAX_CHAR_MS,
          Math.max(MIN_CHAR_MS, TOTAL_TYPE_MS / Math.max(1, target.length)),
        );
        const id = window.setTimeout(() => {
          setState((s) => ({ ...s, text: target.slice(0, s.text.length + 1) }));
        }, charMs);
        return () => window.clearTimeout(id);
      }
      setState((s) => ({ ...s, phase: "dwell" }));
      return;
    }

    // phase === 'dwell'
    if (text.length < target.length) {
      // Target grew into a superset of the held text (e.g. a phase message
      // extended by a copy edit): resume typing instead of freezing.
      setState((s) => ({ ...s, phase: "typing" }));
      return;
    }
    if (!rotate) return; // hold forever, caret keeps blinking
    const id = window.setTimeout(() => {
      setState((s) => ({ ...s, phase: "erasing" }));
    }, DWELL_MS);
    return () => window.clearTimeout(id);
  }, [reducedMotion, text, phase, messageIndex, target, rotate, stableMessages]);

  if (reducedMotion) {
    // Static: current target shown in full, no rotation, no partial text.
    return { text: target, phase: "dwell", messageIndex };
  }
  return state;
}
